import { type CSSProperties, useLayoutEffect, useRef, useState } from "react";
import { type NameSpacing, nameLineEms } from "./nameLineEms.ts";
import "./profile.css";

/**
 * The account's name beside its avatar: at most two lines, no word broken,
 * shrinking to the shared floor (`profile.nameMinimumScale`) before it is
 * cut — the rule iOS sets its one line by, with a second line conceded to
 * the web's narrower slot. The alternative was what the web did: wrap
 * without end, and break inside a word once a word outgrew the slot, which
 * on a phone turned a long single-word name into a column of four-letter
 * fragments as tall as the screen.
 *
 * The parent must be a `.profile-name-slot`: the slot is the container the
 * stylesheet measures the name against, and it has to own its width rather
 * than take it from the name.
 */
export function ProfileName({ name, className = "" }: { name: string; className?: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  /**
   * What the reader's own stylesheet adds, once it has been measured —
   * remembered against the name it was measured for, so a new name is laid
   * out on the plain estimate again and measured afresh.
   */
  const [measured, setMeasured] = useState<({ name: string } & NameSpacing) | null>(null);
  const spacing = measured?.name === name ? measured : {};

  /*
   * The estimate is made before there is a document — that is the whole
   * point of it, the server renders the name at its right size — so it
   * knows the face's advances and nothing about the reader's browser. Ask
   * for WCAG's text spacing (0.12em between letters, 0.16em between words)
   * and an ordinary two-word name needs a third line, while this heading
   * keeps two and hides the rest: seen at 390, "Roma Example" set as "Roma
   * / Exam…" at the full 36px, with the whole of the shrinking this
   * stylesheet exists for never used.
   *
   * So when the name does not fit, read what the spacing actually came out
   * as and ask the estimate again with it. A page as designed measures
   * once, finds the name fits, and changes nothing.
   */
  useLayoutEffect(() => {
    const element = heading.current;
    // `-webkit-line-clamp` holds the box at two lines and hides the rest,
    // so what overflows is exactly what the reader has lost.
    if (!element || element.scrollHeight <= element.clientHeight + 1) return;

    const style = getComputedStyle(element);
    const size = Number.parseFloat(style.fontSize);
    if (!(size > 0)) return;
    // Wider only. The page's own tracking is negative and already in the
    // table's margin; feeding that back would enlarge the name instead.
    const letter = Math.max(0, Number.parseFloat(style.letterSpacing) / size || 0);
    const word = Math.max(0, Number.parseFloat(style.wordSpacing) / size || 0);
    if (!letter && !word) return;
    setMeasured({ name, letter, word });
  }, [name]);

  return (
    <h1
      ref={heading}
      className={`profile-name font-serif tracking-tight ${className}`.trim()}
      style={{ "--name-ems": nameLineEms(name, spacing) } as CSSProperties}
    >
      {name}
    </h1>
  );
}
